import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, Download, AlertTriangle, CheckCircle, Info, Sparkles, RefreshCw, Server, Clock } from 'lucide-react';
import { format } from 'date-fns';

interface AIAnalysisResult {
  id: number;
  analysisId: string;
  analysisType: string;
  priority: string;
  summary: string;
  recommendations: any[];
  nextActions: any[];
  systemData: any;
  aiResponse: string;
  createdAt: string;
  updatedAt: string;
}

interface SystemIssue {
  id: string;
  type: 'critical' | 'warning' | 'info';
  category: string;
  title: string;
  description: string;
  solution: string;
  affectedComponent: string;
  timestamp: Date;
}

interface AIAdvisorPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onRunAnalysis: () => void;
  isAnalyzing: boolean;
  currentAnalysis?: {
    recommendations: any[];
    priority: 'low' | 'medium' | 'high' | 'critical';
    summary: string;
    nextActions: any[];
  } | null;
}

const AIAdvisorPanel: React.FC<AIAdvisorPanelProps> = ({
  isOpen,
  onClose,
  onRunAnalysis,
  isAnalyzing,
  currentAnalysis
}) => {
  const [analysisHistory, setAnalysisHistory] = useState<AIAnalysisResult[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedAnalysis, setExpandedAnalysis] = useState<string | null>(null);
  const [systemIssues, setSystemIssues] = useState<SystemIssue[]>([]);
  const [loadingIssues, setLoadingIssues] = useState(false);
  const [analysisStage, setAnalysisStage] = useState(0);

  // Analysis stages for progress indication
  const analysisStages = [
    "Analyzing system data...",
    "Collecting data from audit logs...",
    "Collecting data from health checks...",
    "Collecting data from performance metrics...",
    "Processing analysis results...",
    "Generating recommendations...",
    "Finalizing report..."
  ];

  // Fetch system issues when panel opens (analysis history is built from live runs only)
  useEffect(() => {
    if (isOpen) {
      fetchSystemIssues();
    }
  }, [isOpen]);

  // Cycle through analysis stages while analyzing
  useEffect(() => {
    if (isAnalyzing) {
      setAnalysisStage(0);
      const interval = setInterval(() => {
        setAnalysisStage(prev => (prev + 1) % analysisStages.length);
      }, 2000); // Change stage every 2 seconds

      return () => clearInterval(interval);
    } else {
      setAnalysisStage(0);
      return undefined;
    }
  }, [isAnalyzing, analysisStages.length]);

  // Add current analysis to history when it completes
  useEffect(() => {
    if (currentAnalysis && !isAnalyzing) {
      const newAnalysis: AIAnalysisResult = {
        id: Date.now(),
        analysisId: `live-${Date.now()}`,
        analysisType: 'System Analysis',
        priority: currentAnalysis.priority,
        summary: currentAnalysis.summary,
        recommendations: currentAnalysis.recommendations,
        nextActions: currentAnalysis.nextActions,
        systemData: {},
        aiResponse: JSON.stringify(currentAnalysis, null, 2),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setAnalysisHistory(prev => [newAnalysis, ...prev]);
    }
  }, [currentAnalysis, isAnalyzing]);

  const fetchAnalysisHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await fetch('/api/parser/history?limit=10', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          const mappedData: AIAnalysisResult[] = data.data.map((item: any) => ({
            id: item.id,
            analysisId: `parser-${item.id}`,
            analysisType: 'Parser Ingestion',
            priority: item.status === 'completed' ? 'low' : 'medium',
            summary: `Framework ingestion: ${item.framework_name || 'Unknown framework'} (${item.status || 'unknown'})`,
            recommendations: [],
            nextActions: [],
            systemData: {
              frameworkCode: item.framework_code,
              templateName: item.template_name,
              createdBy: item.created_by_email
            },
            aiResponse: JSON.stringify(item),
            createdAt: item.created_at,
            updatedAt: item.created_at
          }));

          // Sort by createdAt descending (newest first)
          const sortedData = mappedData.sort((a: AIAnalysisResult, b: AIAnalysisResult) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          setAnalysisHistory(sortedData);

          // Auto-expand the newest analysis
          const firstAnalysis = sortedData[0];
          if (firstAnalysis) {
            setExpandedAnalysis(firstAnalysis.id.toString());
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch analysis history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchSystemIssues = async () => {
    setLoadingIssues(true);
    try {
      // Fetch real system data from multiple endpoints
      const [auditLogsResponse, healthResponse, alertsResponse, metricsResponse] = await Promise.all([
        fetch('/api/admin/audit-logs?limit=50', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/health-check/system/health', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/monitoring/alerts?limit=20', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('/api/monitoring/metrics/summary', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      const issues: SystemIssue[] = [];

      // Analyze audit logs for issues
      if (auditLogsResponse.ok) {
        const auditData = await auditLogsResponse.json();
        if (auditData.data && auditData.data.length > 0) {
          const criticalAudits = auditData.data.filter((log: any) =>
            log.severity === 'critical' || log.type === 'error'
          );
          if (criticalAudits.length > 0) {
            issues.push({
              id: `audit-critical-${Date.now()}`,
              type: 'critical',
              category: 'Security',
              title: `${criticalAudits.length} Critical Audit Events Detected`,
              description: 'Recent audit logs show critical security events that require immediate attention.',
              solution: 'Review audit logs and investigate each critical event. Implement security measures to prevent recurrence.',
              affectedComponent: 'Audit System',
              timestamp: new Date()
            });
          }
        }
      }

      // Analyze system health
      if (healthResponse.ok) {
        const healthData = await healthResponse.json();
        if (healthData.data && healthData.data.summary) {
          const healthScore = healthData.data.summary.score || 0;
          if (healthScore < 70) {
            issues.push({
              id: `health-critical-${Date.now()}`,
              type: 'critical',
              category: 'System Health',
              title: `Critical System Health: ${healthScore}%`,
              description: 'Overall system health has dropped below acceptable levels.',
              solution: 'Check system health dashboard for detailed component status. Restart affected services if necessary.',
              affectedComponent: 'System Health',
              timestamp: new Date()
            });
          } else if (healthScore < 90) {
            issues.push({
              id: `health-warning-${Date.now()}`,
              type: 'warning',
              category: 'System Health',
              title: `System Health Degraded: ${healthScore}%`,
              description: 'System performance is below optimal levels.',
              solution: 'Monitor system metrics and optimize resource allocation. Consider scaling up resources if needed.',
              affectedComponent: 'System Health',
              timestamp: new Date()
            });
          }
        }
      }

      // Analyze alerts
      if (alertsResponse.ok) {
        const alertsData = await alertsResponse.json();
        if (alertsData.data && alertsData.data.length > 0) {
          const criticalAlerts = alertsData.data.filter((alert: any) =>
            alert.severity === 'critical' || alert.priority === 'high'
          );
          if (criticalAlerts.length > 0) {
            issues.push({
              id: `alerts-critical-${Date.now()}`,
              type: 'critical',
              category: 'Monitoring',
              title: `${criticalAlerts.length} Critical Alerts Active`,
              description: 'System monitoring has detected critical alerts requiring immediate action.',
              solution: 'Access monitoring dashboard to review and resolve critical alerts. Set up alert notifications for proactive monitoring.',
              affectedComponent: 'Alert System',
              timestamp: new Date()
            });
          }
        }
      }

      // Analyze metrics
      if (metricsResponse.ok) {
        const metricsData = await metricsResponse.json();
        if (metricsData.data) {
          const apiResponseTime = metricsData.data.api?.responseTime || 0;
          const errorRate = metricsData.data.errors?.rate || 0;
          const cpuUsage = metricsData.data.cpu?.usage || 0;

          if (apiResponseTime > 2000) {
            issues.push({
              id: `api-critical-${Date.now()}`,
              type: 'critical',
              category: 'Performance',
              title: `API Response Time Critical: ${apiResponseTime}ms`,
              description: 'API response times are significantly above acceptable limits.',
              solution: 'Optimize database queries, implement caching, and consider load balancing. Review slow endpoints.',
              affectedComponent: 'API Performance',
              timestamp: new Date()
            });
          } else if (apiResponseTime > 1000) {
            issues.push({
              id: `api-warning-${Date.now()}`,
              type: 'warning',
              category: 'Performance',
              title: `API Response Time Elevated: ${apiResponseTime}ms`,
              description: 'API response times are above optimal levels.',
              solution: 'Profile API endpoints, optimize database queries, and implement response caching.',
              affectedComponent: 'API Performance',
              timestamp: new Date()
            });
          }

          if (errorRate > 5) {
            issues.push({
              id: `error-critical-${Date.now()}`,
              type: 'critical',
              category: 'Stability',
              title: `High Error Rate: ${(errorRate * 100).toFixed(2)}%`,
              description: 'System error rate is critically high.',
              solution: 'Review error logs, identify root causes, and implement fixes. Add error tracking and monitoring.',
              affectedComponent: 'Error Rate',
              timestamp: new Date()
            });
          } else if (errorRate > 1) {
            issues.push({
              id: `error-warning-${Date.now()}`,
              type: 'warning',
              category: 'Stability',
              title: `Elevated Error Rate: ${(errorRate * 100).toFixed(2)}%`,
              description: 'System error rate is above normal levels.',
              solution: 'Monitor error trends, investigate recurring errors, and implement preventive measures.',
              affectedComponent: 'Error Rate',
              timestamp: new Date()
            });
          }

          if (cpuUsage > 80) {
            issues.push({
              id: `cpu-warning-${Date.now()}`,
              type: 'warning',
              category: 'Resources',
              title: `High CPU Usage: ${cpuUsage}%`,
              description: 'Server CPU utilization is high.',
              solution: 'Optimize resource-intensive processes, scale horizontally, or upgrade server resources.',
              affectedComponent: 'CPU Usage',
              timestamp: new Date()
            });
          }
        }
      }

      setSystemIssues(issues);
    } catch (error) {
      console.error('Failed to fetch system issues:', error);
    } finally {
      setLoadingIssues(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    if (!priority) return 'bg-gray-100 text-gray-800 border-gray-200';
    switch (priority.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityIcon = (priority: string) => {
    if (!priority) return <Info className="w-4 h-4" />;
    switch (priority.toLowerCase()) {
      case 'critical':
        return <AlertTriangle className="w-4 h-4" />;
      case 'high':
        return <AlertTriangle className="w-4 h-4" />;
      case 'medium':
        return <Info className="w-4 h-4" />;
      case 'low':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Info className="w-4 h-4" />;
    }
  };

  const toggleExpanded = (id: number) => {
    if (expandedAnalysis === id.toString()) {
      setExpandedAnalysis(null); // Collapse if already expanded
    } else {
      setExpandedAnalysis(id.toString()); // Expand this one (only one at a time)
    }
  };

  const exportAnalysis = (analysis: AIAnalysisResult) => {
    const exportData = {
      analysisId: analysis.analysisId,
      analysisType: analysis.analysisType,
      priority: analysis.priority,
      summary: analysis.summary,
      recommendations: analysis.recommendations,
      nextActions: analysis.nextActions,
      systemData: analysis.systemData,
      aiResponse: analysis.aiResponse,
      createdAt: analysis.createdAt,
      exportedAt: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-analysis-${analysis.analysisId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 bg-black/20 z-[9998]" onClick={onClose} />
    <div className="fixed right-0 top-0 h-full w-[420px] bg-white shadow-2xl border-l border-gray-200 flex flex-col z-[9999]">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5" />
            <h2 className="text-lg font-semibold">AI Advisor</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded-md transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-indigo-100">System analysis and recommendations</p>
      </div>

      {/* Analysis Results Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {/* Show analyzing status at the top when running */}
        {isAnalyzing && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
              <div>
                <p className="text-sm font-medium text-gray-900">{analysisStages[analysisStage]}</p>
                <p className="text-xs text-gray-500">
                  Step {analysisStage + 1} of {analysisStages.length} • Please wait while we analyze your system
                </p>
              </div>
            </div>
          </div>
        )}

        {loadingHistory && analysisHistory.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : analysisHistory.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Sparkles className="w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Analysis Results</h3>
            <p className="text-gray-500 text-sm mb-4">
              Run a system analysis to get AI-powered insights and recommendations.
            </p>
            <button
              onClick={onRunAnalysis}
              disabled={isAnalyzing}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isAnalyzing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Run AI Analysis</span>
                </>
              )}
            </button>
          </div>
        ) : (
          <>
            {/* Analysis Results */}
            <div className="space-y-3">
              {analysisHistory.map((analysis, index) => (
                <div
                  key={analysis.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                >
                  {/* Analysis Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {index === 0 && (
                        <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-green-100 text-green-800 border border-green-200">
                          NEW
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${getPriorityColor(analysis.priority)}`}>
                        {getPriorityIcon(analysis.priority)}
                        {analysis.priority}
                      </span>
                      <span className="text-xs font-medium text-gray-500">
                        {format(new Date(analysis.createdAt), 'MMM dd, HH:mm')}
                      </span>
                    </div>
                    <button
                      onClick={() => exportAnalysis(analysis)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Export analysis"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Summary */}
                  <p className="text-sm text-gray-800 mb-3">{analysis.summary}</p>

                  {/* Expand/Collapse button */}
                  <button
                    onClick={() => toggleExpanded(analysis.id)}
                    className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    {expandedAnalysis === analysis.id.toString() ? (
                      <>
                        <ChevronUp className="w-3 h-3" />
                        Show less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-3 h-3" />
                        Show details
                      </>
                    )}
                  </button>

                  {/* Expanded Details */}
                  {expandedAnalysis === analysis.id.toString() && (
                    <div className="mt-3 space-y-3 pt-3 border-t border-gray-200">
                      {/* Recommendations */}
                      {analysis.recommendations && analysis.recommendations.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-900 mb-2 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            Recommendations
                          </h4>
                          <div className="space-y-2">
                            {analysis.recommendations.map((rec: any, index: number) => (
                              <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <p className="text-xs text-blue-900 font-medium mb-1">{rec.title || `Recommendation ${index + 1}`}</p>
                                {rec.description && <p className="text-xs text-blue-700">{rec.description}</p>}
                                {rec.action && (
                                  <p className="text-xs text-blue-600 mt-1 font-medium">→ {rec.action}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Next Actions */}
                      {analysis.nextActions && analysis.nextActions.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-900 mb-2 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Next Actions
                          </h4>
                          <div className="space-y-2">
                            {analysis.nextActions.map((action: any, index: number) => (
                              <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <p className="text-xs text-green-900 font-medium">{action.title || action.text || `Action ${index + 1}`}</p>
                                {action.priority && (
                                  <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-bold rounded ${getPriorityColor(action.priority)}`}>
                                    {action.priority}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Analysis Insights */}
                      {analysis.aiResponse && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-900 mb-2 flex items-center gap-1">
                            <Info className="w-3 h-3" />
                            Detailed Analysis
                          </h4>
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="text-xs text-blue-900 space-y-3">
                              {(() => {
                                try {
                                  const data = typeof analysis.aiResponse === 'string'
                                    ? JSON.parse(analysis.aiResponse)
                                    : analysis.aiResponse;

                                  return (
                                    <>
                                      {/* Summary */}
                                      {data.summary && (
                                        <div>
                                          <p className="font-medium mb-2">Summary:</p>
                                          <p className="text-blue-800">{data.summary}</p>
                                        </div>
                                      )}

                                      {/* Recommendations */}
                                      {data.recommendations && data.recommendations.length > 0 && (
                                        <div>
                                          <p className="font-medium mb-2">Key Recommendations:</p>
                                          <div className="space-y-2">
                                            {data.recommendations.map((rec: any, index: number) => (
                                              <div key={index} className="bg-white/50 rounded p-2">
                                                <div className="flex items-center gap-2 mb-1">
                                                  <span className={`px-1.5 py-0.5 text-xs font-bold rounded ${
                                                    rec.type === 'critical' ? 'bg-red-100 text-red-800' :
                                                    rec.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                                                    'bg-blue-100 text-blue-800'
                                                  }`}>
                                                    {rec.type}
                                                  </span>
                                                  <span className="font-medium">{rec.title}</span>
                                                </div>
                                                <p className="text-blue-700 mb-1">{rec.description}</p>
                                                {rec.action && (
                                                  <p className="text-blue-600 font-medium">→ {rec.action}</p>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {/* Next Actions */}
                                      {data.nextActions && data.nextActions.length > 0 && (
                                        <div>
                                          <p className="font-medium mb-2">Next Steps:</p>
                                          <div className="space-y-1">
                                            {data.nextActions.map((action: any, index: number) => (
                                              <div key={index} className="flex items-start gap-2">
                                                <span className={`px-1.5 py-0.5 text-xs font-bold rounded mt-0.5 ${
                                                  action.priority === 'critical' ? 'bg-red-100 text-red-800' :
                                                  action.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                                  action.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                                  'bg-green-100 text-green-800'
                                                }`}>
                                                  {action.priority}
                                                </span>
                                                <div>
                                                  <p className="text-blue-800">{action.text}</p>
                                                  {action.responsible && (
                                                    <p className="text-xs text-blue-600">Responsible: {action.responsible}</p>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  );
                                } catch (e) {
                                  // Fallback to plain text if JSON parsing fails
                                  return (
                                    <div className="text-xs text-blue-900 leading-relaxed">
                                      {analysis.aiResponse.split('\n').map((line: string, index: number) => (
                                        <p key={index} className="mb-2 last:mb-0">
                                          {line.trim() || <br />}
                                        </p>
                                      ))}
                                    </div>
                                  );
                                }
                              })()}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* System Health Overview */}
                      {analysis.systemData && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-900 mb-2 flex items-center gap-1">
                            <Server className="w-3 h-3" />
                            System Health Summary
                          </h4>
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="text-xs text-green-900 space-y-2">
                              {(() => {
                                try {
                                  const data = typeof analysis.systemData === 'string'
                                    ? JSON.parse(analysis.systemData)
                                    : analysis.systemData;

                                  // Extract meaningful insights from system data
                                  const insights = [];
                                  if (data.database) {
                                    insights.push(`Database: ${data.database.status || 'Operational'}`);
                                  }
                                  if (data.api) {
                                    insights.push(`API Services: ${data.api.status || 'Running'}`);
                                  }
                                  if (data.memory) {
                                    insights.push(`Memory Usage: ${data.memory.used || 'N/A'} / ${data.memory.total || 'N/A'}`);
                                  }
                                  if (data.cpu) {
                                    insights.push(`CPU Load: ${data.cpu.usage || 'N/A'}%`);
                                  }
                                  if (data.uptime) {
                                    insights.push(`System Uptime: ${data.uptime}`);
                                  }

                                  return insights.length > 0 ? (
                                    insights.map((insight, index) => (
                                      <div key={index} className="flex items-center gap-2">
                                        <CheckCircle className="w-3 h-3 text-green-600" />
                                        <span>{insight}</span>
                                      </div>
                                    ))
                                  ) : (
                                    <p>System operating normally with all services running.</p>
                                  );
                                } catch (e) {
                                  return <p>System analysis completed successfully.</p>;
                                }
                              })()}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {/* System Issues Section */}
        {systemIssues.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-500" />
                Detected System Issues
              </h4>
              <button
                onClick={fetchSystemIssues}
                disabled={loadingIssues}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1 disabled:opacity-50"
              >
                <RefreshCw className={`w-3 h-3 ${loadingIssues ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {systemIssues.map((issue, index) => (
                <div
                  key={`issue-${index}-${issue.id}-${issue.type}`}
                  className={`p-3 rounded-lg border ${
                    issue.type === 'critical'
                      ? 'bg-red-50 border-red-200'
                      : issue.type === 'warning'
                      ? 'bg-yellow-50 border-yellow-200'
                      : 'bg-blue-50 border-blue-200'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                      issue.type === 'critical'
                        ? 'bg-red-500'
                        : issue.type === 'warning'
                        ? 'bg-yellow-500'
                        : 'bg-blue-500'
                    }`}></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-gray-500 bg-white/50 px-2 py-0.5 rounded">
                          {issue.category}
                        </span>
                        <h5 className="text-sm font-medium text-gray-900">{issue.title}</h5>
                      </div>
                      <p className="text-xs text-gray-600 mb-2">{issue.description}</p>
                      <div className="bg-white/50 rounded p-2">
                        <p className="text-xs font-medium text-gray-700 mb-1">Solution:</p>
                        <p className="text-xs text-gray-800">{issue.solution}</p>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                        <Server className="w-3 h-3" />
                        <span>{issue.affectedComponent}</span>
                        <span>•</span>
                        <Clock className="w-3 h-3" />
                        <span>{format(issue.timestamp, 'HH:mm')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer - Action Buttons Only */}
      <div className="p-4 bg-white border-t border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between">
          <button
            onClick={onRunAnalysis}
            disabled={isAnalyzing}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isAnalyzing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Analyzing...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                <span>Run New Analysis</span>
              </>
            )}
          </button>
          {analysisHistory.length > 0 && (
            <button
              onClick={() => { setAnalysisHistory([]); setSystemIssues([]); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Clear
            </button>
          )}
          <span className="text-xs text-gray-400">
            {analysisHistory.length} {analysisHistory.length === 1 ? 'analysis' : 'analyses'}
          </span>
        </div>
      </div>
    </div>
    </>
  );
};

export default AIAdvisorPanel;
